import { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, FileText, Mail, FileSignature, LogOut, UserCheck, Bell, Building2, DollarSign, Zap, UserCircle, Plug, ListTodo } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import API from '@/lib/api';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/leads', icon: Users, label: 'Leads' },
  { to: '/buyers', icon: UserCheck, label: 'Buyers' },
  { to: '/tasks', icon: ListTodo, label: 'Tasks' },
  { to: '/wholesalers', icon: Building2, label: 'Wholesalers' },
  { to: '/drip-campaigns', icon: Zap, label: 'Drip Campaigns' },
  { to: '/earnings', icon: DollarSign, label: 'Earnings' },
  { to: '/templates', icon: FileSignature, label: 'Contract Templates' },
  { to: '/email-templates', icon: Mail, label: 'Email Templates' },
  { to: '/integrations', icon: Plug, label: 'API Keys & Integrations' },
  { to: '/profile', icon: UserCircle, label: 'My Profile' },
];

export default function Sidebar({ open = false, onClose = () => {} }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await API.get('/notifications');
      setNotifications(res.data);
    } catch { /* ignore */ }
  }, []);

  const [taskBadge, setTaskBadge] = useState(0);
  const fetchTaskSummary = useCallback(async () => {
    try {
      const res = await API.get('/tasks/summary', { params: { tz_offset: new Date().getTimezoneOffset() } });
      setTaskBadge(res.data.overdue + res.data.today);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const tick = () => { fetchNotifications(); fetchTaskSummary(); };
    tick();
    const interval = setInterval(tick, 60000); // poll every minute
    window.addEventListener('tasks-changed', tick);
    return () => { clearInterval(interval); window.removeEventListener('tasks-changed', tick); };
  }, [fetchNotifications, fetchTaskSummary]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const handleMarkAllRead = async () => {
    try {
      await API.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch { /* ignore */ }
  };

  const handleNotifClick = async (notif) => {
    if (!notif.read) {
      try {
        await API.put(`/notifications/${notif.id}/read`);
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
      } catch { /* ignore */ }
    }
    if (notif.type === 'task_due') {
      navigate('/tasks');
      setShowNotifs(false);
    } else if (notif.lead_id) {
      navigate(`/leads/${notif.lead_id}`);
      setShowNotifs(false);
    }
  };

  return (
    <aside
      data-testid="sidebar-nav"
      onClick={e => { if (e.target.closest('a')) onClose(); }}
      className={`fixed left-0 top-0 h-screen w-64 bg-[#0D131F] border-r border-[#1F293D] flex flex-col z-40 overflow-y-auto pt-[env(safe-area-inset-top)] transition-transform duration-200 md:translate-x-0 ${open ? 'translate-x-0' : '-translate-x-full'}`}
    >
      <div className="p-6 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#3B82F6] to-[#8B5CF6] flex items-center justify-center shadow-[0_0_18px_rgba(99,102,241,0.45)]">
            <Building2 className="h-4.5 w-4.5 text-white" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white leading-tight" style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-0.03em' }}>
              WholesaleOffers
            </h1>
          </div>
        </div>
        {/* Notification Bell */}
        <div className="relative">
          <button
            data-testid="notification-bell"
            onClick={() => setShowNotifs(!showNotifs)}
            className="relative p-1.5 rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
          >
            <Bell className="h-5 w-5" strokeWidth={1.5} />
            {unreadCount > 0 && (
              <span data-testid="notification-count" className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1">
                {unreadCount}
              </span>
            )}
          </button>

          {/* Notification Dropdown */}
          {showNotifs && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowNotifs(false)} />
              <div data-testid="notification-dropdown" className="absolute left-0 top-full mt-1 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-900">Notifications</p>
                  {unreadCount > 0 && (
                    <button
                      data-testid="mark-all-read-btn"
                      onClick={handleMarkAllRead}
                      className="text-xs text-[#0073ea] hover:underline"
                    >
                      Mark all read
                    </button>
                  )}
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-8">No notifications</p>
                  ) : (
                    notifications.slice(0, 10).map(n => (
                      <button
                        key={n.id}
                        data-testid={`notification-item-${n.id}`}
                        onClick={() => handleNotifClick(n)}
                        className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${!n.read ? 'bg-blue-50/50' : ''}`}
                      >
                        <p className={`text-xs font-semibold ${!n.read ? 'text-gray-900' : 'text-gray-600'}`}>{n.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.message}</p>
                        <p className="text-[10px] text-gray-400 mt-1">
                          {new Date(n.created_at).toLocaleString()}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Separator className="bg-[#1F293D]" />

      <nav className="flex-1 p-3 space-y-1">
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === '/'}
            data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive
                  ? 'bg-blue-500/15 !text-blue-300 border-l-[3px] !border-blue-500 shadow-[0_0_18px_rgba(59,130,246,0.18)]'
                  : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/60'
              }`
            }
          >
            <Icon className="h-[18px] w-[18px]" strokeWidth={1.5} />
            {label}
            {to === '/tasks' && taskBadge > 0 && <span data-testid="tasks-nav-badge" className="ml-auto h-5 min-w-[20px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{taskBadge}</span>}
          </NavLink>
        ))}
      </nav>

      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center gap-3 px-2 mb-3">
          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-[#3B82F6] to-[#EC4899] flex items-center justify-center text-white text-sm font-semibold">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
            <p className="text-xs text-gray-400 truncate">{user?.email}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={logout}
          data-testid="logout-btn"
          className="w-full justify-start text-gray-500 hover:text-red-600 hover:bg-red-50"
        >
          <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Sign Out
        </Button>
      </div>
    </aside>
  );
}
